import { resolveId } from '@medplum/core'
import { referenceDisplay } from '@seen/core/display'
import { CarePlanState } from '@seen/core/models/care-plans/constants'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import { IconCircleCheck } from '@tabler/icons-react'
import type { MRT_TableOptions } from 'mantine-react-table'
import { type JSX, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import { DateDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { StatusBadge } from '#src/components/display/StatusBadge.tsx'
import { ExtendedResourceTable } from '#src/components/fhir-table/ExtendedResourceTable.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'

export function CarePlansPage(): JSX.Element {
  const navigate = useNavigateFromClickEvent()
  const [searchParams, setSearchParams] = useSearchParams()
  let activeTab = searchParams.get('tab') ?? 'all'
  if (!['all', 'draft', 'pending', 'active'].includes(activeTab)) {
    activeTab = 'all'
  }

  const columnConfigs = useMemo<ColumnConfig<InitialCarePlan>[]>(
    () => [
      {
        id: 'participant',
        headerText: 'Participant',
        render: (carePlan) => <ResourceCard value={carePlan.patientSubject} />,
        valueType: 'text' as const,
        getSortFilterValue: (carePlan) =>
          referenceDisplay(carePlan.patientSubject) ?? '',
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'status',
        headerText: 'Status',
        render: (carePlan) => (
          <StatusBadge status={carePlan.status} label={carePlan.state} />
        ),
        valueType: 'text' as const,
        getSortFilterValue: (carePlan) => carePlan.state,
        enableSorting: true,
      },
      {
        id: 'type',
        headerText: 'Type',
        render: (carePlan) => <>{carePlan.typeDisplay}</>,
        valueType: 'text' as const,
        getSortFilterValue: (carePlan) => carePlan.typeDisplay,
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'dueDate',
        headerText: 'Due',
        render: (carePlan) => {
          if (carePlan.isIncomplete) {
            return <HoverDate date={carePlan.dueDate} format="relative" />
          } else {
            return <IconCircleCheck className="stroke-green-500" />
          }
        },
        valueType: 'date' as const,
        getSortFilterValue: (carePlan) => carePlan.dueDate,
        filteringType: 'date-range',
        enableSorting: true,
      },
      {
        id: 'createdDate',
        headerText: 'Created',
        render: (carePlan) => <DateDisplay value={carePlan.createdDate} />,
        valueType: 'date' as const,
        getSortFilterValue: (carePlan) => carePlan.createdDate,
        filteringType: 'date-range',
        enableSorting: true,
      },
    ],
    []
  )

  const activeColumnConfigs = useMemo<ColumnConfig<InitialCarePlan>[]>(
    () => [
      ...columnConfigs.filter(
        (c) => c.id !== 'dueDate' && c.id !== 'createdDate'
      ),
      {
        id: 'startDate',
        headerText: 'Start Date',
        render: (carePlan) => <HoverDate date={carePlan.startDate as Date} />,
        valueType: 'date' as const,
        getSortFilterValue: (carePlan) => carePlan.startDate,
        filteringType: 'date-range',
        enableSorting: true,
      },
      {
        id: 'endDate',
        headerText: 'End Date',
        render: (carePlan) => <HoverDate date={carePlan.endDate as Date} />,
        valueType: 'date' as const,
        getSortFilterValue: (carePlan) => carePlan.endDate,
        filteringType: 'date-range',
        enableSorting: true,
      },
    ],
    [columnConfigs]
  )

  const tableOptions = {
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const urlPath = `/Patient/${resolveId(row.original.patientSubject)}/care-plans/${row.original.id}`
        navigate(urlPath, event)
      },
      className: 'cursor-pointer',
    }),
  } satisfies Partial<MRT_TableOptions<InitialCarePlan>>

  return (
    <PageLayout title="Care Plans">
      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setSearchParams((prev) => {
            prev.set('tab', value)
            return prev
          })
        }
        className="flex grow flex-col"
      >
        <TabsList className="mb-4 w-fit print:hidden">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="pending">Pending Signatures</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="grow">
          <ExtendedResourceTable
            resourceClass={InitialCarePlan}
            columnConfigs={columnConfigs}
            filterQuery={InitialCarePlan.searchQuery()}
            tableOptions={tableOptions}
          />
        </TabsContent>

        <TabsContent value="draft" className="grow">
          <ExtendedResourceTable
            resourceClass={InitialCarePlan}
            columnConfigs={columnConfigs}
            filterQuery={InitialCarePlan.byState(CarePlanState.Draft)}
            tableOptions={tableOptions}
          />
        </TabsContent>

        <TabsContent value="pending" className="grow">
          <ExtendedResourceTable
            resourceClass={InitialCarePlan}
            columnConfigs={columnConfigs}
            filterQuery={InitialCarePlan.byState(
              CarePlanState.PendingSignatures
            )}
            tableOptions={tableOptions}
          />
        </TabsContent>

        <TabsContent value="active" className="grow">
          <ExtendedResourceTable
            resourceClass={InitialCarePlan}
            columnConfigs={activeColumnConfigs}
            filterQuery={InitialCarePlan.byState(CarePlanState.Active)}
            tableOptions={tableOptions}
          />
        </TabsContent>
      </Tabs>
    </PageLayout>
  )
}
